import React from 'react';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { MajorAudienceDemographicsIcon } from '../../../../components/icons/major-audience-demographics-icon';
import { MajorAudienceDemographicHighlightTab } from '../major-audience-demographic-highlight-tab';
import type { MajorAudienceDemographicsSectionProps } from './hooks';
import { AgeDistributionView } from './views/age-distribution-view';
import { GenderDistributionView } from './views/gender-distribution-view';
import { CountryDistributionView } from './views/country-distribution-view';
import { CityDistributionView } from './views/city-distribution-view';

interface DemographicsTabProps {
  isActive: boolean;
  onClick: () => void;
  heading: string;
  info: React.ReactNode;
  className?: string;
}

const DemographicsTab: React.FC<DemographicsTabProps> = ({
  isActive,
  onClick,
  heading,
  info,
  className,
}) => (
  <MajorAudienceDemographicHighlightTab
    headingText={heading}
    infoText={info}
    isActive={isActive}
    onClick={onClick}
    className={className}
  />
);

export const MajorAudienceDemographicsSection: React.FC<MajorAudienceDemographicsSectionProps> = ({
  activeDemographicTab,
  setActiveDemographicTab,
  isLoading,
  ageBucket,
  gender,
  country,
  city,
  ageDistributionData,
  genderDistributionData,
  topCountriesData,
  topCitiesData,
}) => {
  const processedGenderInfo = (gender && gender !== '--')
    ? <span className="capitalize">{gender}</span>
    : '--';

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="w-6 h-6 rounded-lg" />
          <Skeleton className="h-5 w-52" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          <div className="md:col-span-1 flex flex-col items-start gap-[2px] self-stretch">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="mb-2 w-full">
                <Skeleton className="h-8 w-full mb-1" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
          <div className="md:col-span-2 border rounded-lg bg-muted/40 min-h-[250px] flex items-center justify-center">
            <Skeleton className="h-40 w-40 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
        <div className="w-6 h-6 p-1 rounded-lg bg-[#5DB8B3] flex items-center justify-center">
          <MajorAudienceDemographicsIcon />
        </div>
        <span>Major Audience Demographics</span>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
        <div className="md:col-span-1 flex flex-col items-start gap-[2px] self-stretch">
          <DemographicsTab
            heading="Age Bucket"
            info={ageBucket ?? '--'}
            isActive={activeDemographicTab === 'age'}
            onClick={() => setActiveDemographicTab('age')}
            className="mb-2"
          />
          <DemographicsTab
            heading="Gender"
            info={processedGenderInfo}
            isActive={activeDemographicTab === 'gender'}
            onClick={() => setActiveDemographicTab('gender')}
            className="mb-2"
          />
          <DemographicsTab
            heading="Country"
            info={country ?? '--'}
            isActive={activeDemographicTab === 'country'}
            onClick={() => setActiveDemographicTab('country')}
            className="mb-2"
          />
          <DemographicsTab
            heading="City"
            info={city ?? '--'}
            isActive={activeDemographicTab === 'city'}
            onClick={() => setActiveDemographicTab('city')}
            className="mb-2"
          />
        </div>
        <div className="md:col-span-2 rounded-lg min-h-[250px] flex flex-col items-start justify-start">
          {activeDemographicTab === 'age' && <AgeDistributionView ageDistributionData={ageDistributionData} />}
          {activeDemographicTab === 'gender' && (
            <GenderDistributionView genderDistributionData={genderDistributionData} />
          )}
          {activeDemographicTab === 'country' && (
            <CountryDistributionView topCountriesData={topCountriesData} />
          )}
          {activeDemographicTab === 'city' && (
            <CityDistributionView topCitiesData={topCitiesData} />
          )}
        </div>
      </div>
    </div>
  );
};

MajorAudienceDemographicsSection.displayName = 'MajorAudienceDemographicsSection';
